/**
 * Which clock this customer reads, and where that fact is kept.
 *
 * Every timestamp the API sends is UTC, and every one of them used to be
 * printed in whatever zone the reading device happened to be set to. On a
 * desk in Sarajevo that was right by coincidence. On a shop-floor phone still
 * on the UTC its ROM shipped with, a submission made at 08:15 read 07:15, and
 * "today" ended an hour early — the same failure the MES delivery log showed,
 * where 04:40 Belgrade time was printed as 03:40. The zone is now a company
 * setting, chosen by the glavni administrator, and the server formats its own
 * reports, e-mails and MES messages in it. This is the browser's half.
 *
 * Shaped like `lib/currency.ts`, and for the same reasons: `lib/utils.ts`
 * formats dates outside any component, so the value has to be readable without
 * a hook, and the rules below can then be tested without a DOM. Not stored
 * either — it is a property of the company, and the only correct value is
 * whatever `/auth/me` last said.
 *
 * Until a session exists, and for an account with no company (the owner), the
 * browser's own zone is used. That is what every formatter did before, so a
 * screen nobody has signed into looks exactly as it always has.
 */

/** The zone the device itself is set to. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** Null means "follow the device" — see the docblock. */
let activeTimeZone: string | null = null

/**
 * Whether this browser can format in the zone.
 *
 * Asked of `Intl` itself rather than checked against a list, because the list
 * is not the same thing: `Intl` accepts every alias it knows, and an older
 * browser does not know a zone renamed after it shipped (`Europe/Kyiv` arrived
 * in 2022). Formatting in a zone the engine rejects throws, and one unknown
 * name must not take every date on every screen down with it.
 */
export function isValidTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone === '') return false

  try {
    new Intl.DateTimeFormat('en', { timeZone: zone })

    return true
  } catch {
    return false
  }
}

/**
 * Take the zone from a signed-in session.
 *
 * Called wherever the user is set, sign-out included: passing nothing returns
 * to the device's zone, so the sign-in screen of a shared terminal does not
 * keep the last person's company. A zone this browser cannot format in falls
 * back the same way, rather than throwing out of every date on the page.
 */
export function adoptCompanyTimeZone(zone: string | null | undefined): void {
  activeTimeZone = isValidTimeZone(zone) ? zone : null
}

/** The zone every date in the application is printed in. */
export function getActiveTimeZone(): string {
  return activeTimeZone ?? browserTimeZone()
}

// ---------------------------------------------------------------------------
// The calendar, in that zone
// ---------------------------------------------------------------------------

export interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

/*
 * One formatter per zone, kept. Building an `Intl.DateTimeFormat` is the
 * expensive half of formatting, and grouping fifty notifications by day would
 * otherwise build fifty identical ones.
 */
const partsFormatters = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = partsFormatters.get(zone)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      // h23, not `hour12: false`: the latter prints midnight as "24" in some
      // engines, which would put the first minutes of a day on the day before.
      hourCycle: 'h23',
    })
    partsFormatters.set(zone, formatter)
  }

  return formatter
}

/**
 * What a clock on the factory wall shows at a given moment.
 *
 * Read out of `formatToParts` rather than computed from an offset, because the
 * offset is the part that moves: twice a year, and on different dates in
 * different zones. `Intl` already knows all of that.
 */
export function wallClock(
  at: Date | string | number = new Date(),
  zone: string = getActiveTimeZone(),
): WallClock {
  const parts = partsFormatter(zone).formatToParts(new Date(at))
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0)

  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour') % 24,
    minute: part('minute'),
  }
}

const pad = (value: number) => String(value).padStart(2, '0')

/**
 * A moment's calendar date in the zone, `YYYY-MM-DD`.
 *
 * The shape a date input takes and the API's date parameters expect.
 * `toISOString().slice(0, 10)` looks like the same thing and is not: it is the
 * date in UTC, so on any evening after 23:00 in Central Europe — 22:00 in
 * summer — it is already tomorrow, and a form refused today as a due date.
 */
export function calendarDate(
  at: Date | string | number = new Date(),
  zone: string = getActiveTimeZone(),
): string {
  const { year, month, day } = wallClock(at, zone)

  return `${year}-${pad(month)}-${pad(day)}`
}

/** Today, in the company's zone. */
export function today(zone: string = getActiveTimeZone()): string {
  return calendarDate(new Date(), zone)
}

/** The year it is now in the company's zone. */
export function currentYear(zone: string = getActiveTimeZone()): number {
  return wallClock(new Date(), zone).year
}

/** The time now in the company's zone, `HH:MM`. */
export function clockTime(
  at: Date | string | number = new Date(),
  zone: string = getActiveTimeZone(),
): string {
  const { hour, minute } = wallClock(at, zone)

  return `${pad(hour)}:${pad(minute)}`
}

/*
 * Calendar arithmetic on `YYYY-MM-DD`, done in UTC on purpose.
 *
 * These move a *date*, not a moment, so no zone may take part: in a local-time
 * `Date`, the night the clocks change is 23 or 25 hours long, and adding a day
 * of milliseconds can land on the same date or skip one. UTC has no such
 * nights.
 */
function shiftDate(date: string, years: number, days: number): string {
  const [year, month, day] = date.split('-').map(Number)
  const moved = new Date(Date.UTC(year + years, month - 1, day + days))

  return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}`
}

/** A date some whole days away. Negative goes back. */
export function addDays(date: string, days: number): string {
  return shiftDate(date, 0, days)
}

/**
 * A date some whole years away. 29 February lands on 1 March in a year
 * without one, which is what `setFullYear` did before this replaced it.
 */
export function addYears(date: string, years: number): string {
  return shiftDate(date, years, 0)
}

// ---------------------------------------------------------------------------
// The list a glavni administrator chooses from
// ---------------------------------------------------------------------------

/**
 * The zones this application's customers are actually in, offered first.
 *
 * The full list is four hundred names long and sorted by continent, so the
 * one somebody in Banja Luka wants would otherwise sit between Europe/Athens
 * and Europe/Berlin under a heading nobody scrolls to. UTC last, for the
 * customer whose MES runs on it.
 */
export const COMMON_TIME_ZONES = [
  'Europe/Belgrade',
  'Europe/Sarajevo',
  'Europe/Zagreb',
  'Europe/Ljubljana',
  'Europe/Podgorica',
  'Europe/Skopje',
  'Europe/Vienna',
  'Europe/Berlin',
  'Europe/London',
  'UTC',
] as const

/** What a company that never chose gets, matching the server's default. */
export const DEFAULT_TIME_ZONE = 'Europe/Belgrade'

/**
 * The browser's old names for zones that have been renamed, mapped to the
 * names the server accepts.
 *
 * `Intl.supportedValuesOf('timeZone')` lists CLDR's identifiers, and CLDR
 * still calls Kyiv "Europe/Kiev" and Kolkata "Asia/Calcutta". The server
 * validates against PHP's `DateTimeZone::listIdentifiers()`, which uses the
 * current names and leaves the old ones out — so a zone picked straight from
 * the browser's list came back as a 422 for a choice the screen itself had
 * offered. Measured against PHP 8.5 and Node 22; a browser that already lists
 * the new names passes through untouched.
 */
const RENAMED_ZONES: Record<string, string> = {
  'Africa/Asmera': 'Africa/Asmara',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Coral_Harbour': 'America/Atikokan',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Godthab': 'America/Nuuk',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
}

/**
 * Every zone, by the name the server stores: the common ones first, then the
 * rest in the browser's order.
 *
 * `supportedValuesOf` is missing from browsers before 2022; there the common
 * list is all that is offered, plus whatever the company already has, which
 * is still every zone a customer of this application has asked for.
 */
export function timeZoneChoices(current?: string | null): { common: string[]; other: string[] } {
  const common: string[] = [...COMMON_TIME_ZONES]

  let all: string[] = []
  try {
    all = Intl.supportedValuesOf('timeZone')
  } catch {
    all = []
  }

  const seen = new Set(common)
  const other: string[] = []

  for (const zone of [...all.map((name) => RENAMED_ZONES[name] ?? name), current ?? '']) {
    if (zone === '' || seen.has(zone)) continue
    seen.add(zone)
    other.push(zone)
  }

  return { common, other }
}

/**
 * The zone's distance from UTC right now, "UTC+01:00".
 *
 * Right now, because that is the only honest answer: Belgrade is +01:00 in
 * November and +02:00 in July. Read from `Intl` rather than computed, for the
 * reason `wallClock` gives.
 */
export function utcOffset(zone: string, at: Date = new Date()): string {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value

    // "GMT+01:00", or a bare "GMT" for UTC itself.
    return name ? name.replace(/^GMT/, 'UTC').replace(/^UTC$/, 'UTC+00:00') : ''
  } catch {
    return ''
  }
}
